import React, { useState, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

const THRESHOLD = 140;

export default function PullToRefresh({ onRefresh, children }) {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const containerRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (containerRef.current?.scrollTop === 0) {
      startY.current = e.touches[0].clientY;
      setPulling(true);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!pulling || refreshing) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) {
      setPullDistance(Math.min(dy * 0.35, THRESHOLD * 1.2));
    }
  }, [pulling, refreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (!pulling) return;
    setPulling(false);
    if (pullDistance >= THRESHOLD && onRefresh) {
      setRefreshing(true);
      try { await onRefresh(); } catch { /* ignore */ }
      setRefreshing(false);
    }
    setPullDistance(0);
  }, [pulling, pullDistance, onRefresh]);

  const showIndicator = pullDistance > 10 || refreshing;
  const progress = refreshing ? undefined : Math.min((pullDistance / THRESHOLD) * 100, 100);

  return (
    <Box
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      sx={{ flex: 1, overflow: 'auto', position: 'relative' }}
    >
      {showIndicator && (
        <Box sx={{
          display: 'flex', justifyContent: 'center', py: 1,
          height: refreshing ? 48 : pullDistance * 0.5,
          transition: pulling ? 'none' : 'height 0.2s',
          overflow: 'hidden',
          alignItems: 'center',
        }}>
          <CircularProgress
            size={24}
            variant={refreshing ? 'indeterminate' : 'determinate'}
            value={progress}
          />
        </Box>
      )}
      {children}
    </Box>
  );
}
